import { Divider, Typography } from '@mui/material'
import { InviteMinor } from '@shopify/polaris-icons'
import { Formik } from 'formik'
import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { BasicSelectOption } from 'src/components/shared/form/BasicSelect'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { BasicModalDescription, BasicModalHeader } from 'src/components/shared/modal'
import { WarningContainer } from 'src/components/shared/WarningContainer'
import { OfferCandidates } from 'src/constants/actionTypes'
import { OpportunityCandidate, OpportunityOfferResult } from 'src/interfaces/opportunity'
import { getOnboardingTemplates } from 'src/selectors/onboarding'
import { colors } from 'src/theme'
import { addS } from 'src/utils/baseUtils'
import { handleApiError } from 'src/utils/errorHandlers'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { showToast } from 'src/utils/toast'
import { OfferCandidatesFormValues } from './OfferCandidatesForm.interface'
import { OfferCandidatesSchema } from './OfferCandidatesForm.validator'

/**
 * Mirrors OPPORTUNITY_OFFER_MAX_BATCH on the backend. Offers are processed one candidate at a time there, so a
 * large selection is split across requests rather than sitting on a single one until it times out.
 */
const OFFER_BATCH_SIZE = 25

const chunk = <T,>(items: Array<T>, size: number): Array<Array<T>> => {
  const chunks: Array<Array<T>> = []

  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }

  return chunks
}

interface Props {
  candidates: Array<OpportunityCandidate>
  onClose(): void
  onOffered(): void
}

export const OfferCandidatesForm = ({ candidates, onClose, onOffered }: Props) => {
  const dispatch = useDispatch()

  const onboardingTemplates = useSelector(getOnboardingTemplates)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const templateOptions = useMemo<Array<BasicSelectOption>>(() => {
    return (onboardingTemplates || []).map((template: any) => {
      return {
        value: template.id,
        label: template.title || template.name,
      }
    })
  }, [onboardingTemplates])

  const initialValues = useMemo<OfferCandidatesFormValues>(() => {
    return {
      // With one template there is nothing to choose, so it is pre-selected.
      collectDataTemplateId: onboardingTemplates?.length === 1 ? onboardingTemplates[0].id : '',
    }
  }, [onboardingTemplates])

  const handleFormSubmit = async (values: OfferCandidatesFormValues) => {
    setIsLoading(true)

    const result: OpportunityOfferResult = { offered: [], skipped: [], failed: [] }

    try {
      const allCandidateIds = candidates.map((candidate) => {
        return candidate.id
      })
      const batches = chunk(allCandidateIds, OFFER_BATCH_SIZE)

      // Sequential, not parallel: each batch creates contractors, and firing them together would multiply the
      // load for no gain.
      for (const candidateIds of batches) {
        const batchResult: OpportunityOfferResult = await createAsyncAction(
          dispatch,
          OfferCandidates.request({
            candidateIds,
            collectDataTemplateId: values.collectDataTemplateId,
          }),
        )

        result.offered.push(...(batchResult?.offered || []))
        result.skipped.push(...(batchResult?.skipped || []))
        result.failed.push(...(batchResult?.failed || []))
      }

      const offeredCount = result.offered.length
      const failedCount = result.failed.length

      if (offeredCount > 0) {
        showToast(
          `${offeredCount} ${addS(offeredCount, 'offer')} sent. ${addS(
            offeredCount,
            'Contractor',
          )} created and the invite is on the way.`,
          { variant: 'success' },
        )
      }

      // Reported separately rather than as one generic failure: a batch can partly succeed, and the customer
      // needs to know exactly which candidates still need attention.
      if (failedCount > 0) {
        const reasons = result.failed
          .map((item) => {
            return item.reason
          })
          .filter(Boolean)

        showToast(`${failedCount} could not be offered${reasons.length ? `: ${reasons[0]}` : ''}`, {
          variant: 'error',
        })
      }

      onOffered()
      onClose()
    } catch (err: any) {
      handleApiError(err)

      // Batches before the failure already created contractors. Refresh regardless so those candidates leave
      // the New tab and cannot be offered a second time.
      if (result.offered.length) {
        onOffered()
        onClose()
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      validationSchema={OfferCandidatesSchema}
    >
      {({ handleSubmit }) => (
        <div css={tw`max-w-[528px]`} data-testid="candidate-pool-offer-modal">
          <BasicModalHeader
            title={
              candidates.length > 1
                ? `Make an offer to ${candidates.length} candidates`
                : 'Make an offer'
            }
            onClose={onClose}
            closeButtonDataTestId="candidate-pool-offer-modal-close-btn"
          />
          <BasicModalDescription
            title="Start onboarding for the people you picked"
            icon={<InviteMinor width={20} height={20} />}
            description="Each one becomes a contractor and gets an invite to start onboarding."
          />
          <Divider css={tw`mt-4 mx-4 border-[#EDEDED]`} />

          <div css={tw`p-4 flex flex-col gap-6`}>
            <div>
              <FormikSelect<OfferCandidatesFormValues>
                name="collectDataTemplateId"
                label="Onboarding template"
                options={templateOptions}
                inputProps={{ 'data-testid': 'candidate-pool-offer-template' } as any}
              />
              <Typography variant="body2" color={colors.SHADES_GREY_400} css={tw`mt-1`}>
                Decides which checks and documents onboarding asks for.
              </Typography>
            </div>

            {/* Deliberately a count, not a list: a bulk selection can be large and a scrolling list inside a modal
                reads badly. The detail is already on the row the customer selected from. */}
            <div>
              <Typography css={tw`mb-1`} variant="h2">
                {candidates.length}
              </Typography>
              <Typography variant="body2" color={colors.SHADES_GREY_400}>
                {addS(candidates.length, 'candidate')} selected. Their name, email, phone, vehicle
                type and market carry over from the application — none of it is asked again.
              </Typography>
            </div>

            <WarningContainer
              className="mt-2"
              title="This starts onboarding"
              subtitle="Each offer creates the contractor and sends them an invite. The checks in the template — background check, MVR, and drug screen where configured — run as they progress, and are billable."
            />
          </div>

          <Divider css={tw`mx-4 border-[#EDEDED]`} />
          <div css={tw`px-4 py-3 flex justify-between items-center`}>
            <Button
              className={tw`px-8`}
              size="small"
              color="grey"
              disabled={isLoading}
              onClick={onClose}
            >
              Cancel
            </Button>
            {/* Never disabled on invalid input — clicking surfaces the missing template instead of going dead. The
                loading state still matters: a batch runs one candidate at a time and can sit for several seconds. */}
            <Button
              className={tw`px-8`}
              color="dark"
              size="small"
              loading={isLoading}
              data-testid="candidate-pool-offer-submit"
              onClick={() => {
                handleSubmit()
              }}
            >
              {candidates.length > 1 ? `Send ${candidates.length} offers` : 'Send offer'}
            </Button>
          </div>
        </div>
      )}
    </Formik>
  )
}
